import { FC, useCallback, useContext, useMemo } from 'react'
import { Form, Select } from 'antd'
import { skipToken } from '@reduxjs/toolkit/query'
import useFormInstance from 'antd/es/form/hooks/useFormInstance'
import { useGetMaterialsQuery } from '../../../../../../store/api/materials-api'
import { EditableCellCtx } from '../../../../../../components/editable-cell/editable-cell'
import { DataSourceItem } from '../../../../helpers/convert-calculations-to-data-source'
import calculateStockAmount from '../../../../../../helpers/entities/stock/calculate-stock-amount'
import MaterialType from '../../../../../../types/api/material-type'
import MaterialWidthOptionLabel from '../../../../../../components/materials/material-width-option-label'
import useGroupedMaterialsByType from '../../../../../../helpers/render/use-grouped-materials-by-type'
import useMaterialWidthOptions from '../../../../../../helpers/render/use-material-width-options'
import type MaterialEntity from '../../../../../../types/api/material'
import { StrapiDataItem } from '../../../../../../types/strapi/strapi'

interface AdditionalMaterialsProps {
    record?: DataSourceItem
}

const AdditionalMaterials: FC<AdditionalMaterialsProps> = ({ record }) => {
    const materialTypeDocIds = useMemo(() => {
        const additionalMaterialTypes =
            record && 'additional_material_types' in record
                ? record.additional_material_types || []
                : []

        return additionalMaterialTypes
            .map((type: MaterialType | undefined) => type?.documentId)
            .filter((id: string | undefined): id is string => Boolean(id))
    }, [record])

    const queryArg = useMemo(() => {
        if (materialTypeDocIds.length === 0) {
            return skipToken
        }

        return {
            filters: {
                material_type: {
                    documentId: {
                        $in: materialTypeDocIds,
                    },
                },
            },
            populate: {
                material_type: true,
                stocks: {
                    populate: {
                        stock_reserves: true,
                    },
                },
            },
            pagination: {
                page: 1,
                pageSize: 1000,
            },
        }
    }, [materialTypeDocIds])

    const { data } = useGetMaterialsQuery(queryArg)
    const ctx = useContext(EditableCellCtx)
    const form = useFormInstance()

    const baseOptions = useMaterialWidthOptions<StrapiDataItem<MaterialEntity>>({
        items: data?.data,
        getWidth: item => item.width,
        getDocumentId: item => item.documentId,
        getStock: item =>
            item.stocks?.reduce((sum, stock) => sum + calculateStockAmount(stock), 0) || 0,
    })

    const groupedMaterials = useGroupedMaterialsByType(data?.data ?? [])

    const materialOptions = useMemo(() => {
        const savedMaterials =
            record && 'additional_materials' in record ? record.additional_materials || [] : []
        const optionKeys = new Set(baseOptions.map(opt => opt.key))

        const fallbackOptions = savedMaterials
            .filter(material => material?.documentId && !optionKeys.has(material.documentId))
            .map(material => ({
                key: material.documentId,
                value: material.width,
                label: <MaterialWidthOptionLabel width={material.width} />,
                typeDocId: material.material_type?.documentId,
            }))

        return [...baseOptions, ...fallbackOptions].sort(
            (a, b) => Number(a.value) - Number(b.value)
        )
    }, [baseOptions, record])

    const groupedOptions = useMemo(() => {
        return groupedMaterials
            .map(group => ({
                type: group.type,
                options: materialOptions.filter(opt => {
                    if (group.materials.some(m => m.documentId === opt.key)) {
                        return true
                    }

                    return 'typeDocId' in opt && opt.typeDocId === group.type.documentId
                }),
            }))
            .filter(group => group.options.length > 0)
    }, [groupedMaterials, materialOptions])

    const allMaterials = useMemo(() => {
        return groupedMaterials.flatMap(group => group.materials)
    }, [groupedMaterials])

    const handleChange = useCallback(
        (selectedDocIds: string[]) => {
            const savedMaterials =
                record && 'additional_materials' in record
                    ? record.additional_materials || []
                    : []
            const byType = new Map<string, string>()

            for (const docId of selectedDocIds) {
                const typeId =
                    allMaterials.find(m => m.documentId === docId)?.material_type?.documentId ??
                    savedMaterials.find(m => m.documentId === docId)?.material_type?.documentId

                if (typeId) {
                    byType.set(typeId, docId)
                }
            }

            form.setFieldValue(['additional_materials', 'set'], Array.from(byType.values()))
        },
        [allMaterials, form, record]
    )

    const filterOption = useCallback(
        (input: string, option?: { value?: string | number }) => {
            const opt = materialOptions.find(o => o.key === option?.value)
            return opt ? String(opt.value).includes(input.trim()) : false
        },
        [materialOptions]
    )

    return (
        <Form.Item name={['additional_materials', 'set']} noStyle>
            <Select
                mode='multiple'
                autoFocus
                onBlur={() => ctx.onBlur()}
                onChange={handleChange}
                style={{ width: '200%' }}
                allowClear
                dropdownStyle={{ width: '475px' }}
                showSearch
                maxTagCount='responsive'
                filterOption={filterOption}
                notFoundContent={
                    materialTypeDocIds.length === 0
                        ? 'Сначала укажите типы дополнительных материалов'
                        : null
                }
                defaultOpen
                placeholder='Выберите ширины'
            >
                {groupedOptions.map(group => (
                    <Select.OptGroup key={group.type.documentId} label={group.type.name}>
                        {group.options.map(opt => (
                            <Select.Option key={opt.key} value={opt.key}>
                                {opt.label}
                            </Select.Option>
                        ))}
                    </Select.OptGroup>
                ))}
            </Select>
        </Form.Item>
    )
}

export default AdditionalMaterials
